import React from "react";
import {
  RocketOutlined,
  CloudUploadOutlined,
  FileSearchOutlined,
  CheckOutlined,
  CalendarOutlined,
  MailOutlined,
  HourglassOutlined,
} from "@ant-design/icons";
import { useCurrentApplication } from "../../../Context/CurrentApplicationContext/CurrentApplicationContext";
import { useAuth } from "../../../Context/AuthContext/AuthContext";
import {
  getLastPendingRoundInfo,
  getScheduleInformation,
} from "../../../Utils/job-application.utils";
import { Typography } from "antd";

const messages = {
  notApplied: {
    title: "Ready to Take the Next Step?",
    message:
      "This role could be a great match for your skills and experience. Apply now to get started with our AI-powered profile analysis and discover how well you fit this opportunity.",
  },
  resumeNotUploaded: {
    title: "Complete Your Application",
    message:
      "You've applied! Upload your resume so we can analyze your profile and match you with this role.",
  },
  applicationRejected: {
    title: "Application Under Review",
    message:
      "Thank you for your interest in this position. Our recruitment team is currently reviewing your application and will get back to you shortly. We appreciate your patience.",
  },
  slotSelection: {
    title: "Interview Invitation",
    message:
      "Congratulations! You are qualified for an online interview with our hiring team. As the next step, please select a convenient time slot to schedule your interview.",
  },
  partiallyQualified: {
    title: "Assessment Completed",
    message:
      "Great work! You’ve successfully completed all the assessments for this role. Our recruitment team is currently reviewing your results and will get back to you shortly with the next steps.",
  },
  assessmentNotGenerated: {
    title: "Waiting for Recruiter Action",
    message:
      "You have successfully qualified the first stage of the hiring process. Our recruitment team will review your application and update you with the next steps soon.",
  },
  shortlisted: {
    title: "You're Shortlisted!",
    message:
      "Great news! Your profile has been shortlisted for this role. Our recruitment team will reach out with the next steps in the hiring process.",
  },
  hired: {
    title: "Congratulations! You're Hired 🎉",
    message:
      "Congratulations! You have been selected for this role. Our team will contact you soon with the onboarding details and next steps.",
  },
};

interface CTAData {
  messageIcon: React.ReactNode;
  heading: string;
  description: string;
}

const ApplicationJourneyNotification: React.FC = () => {
  const { user } = useAuth();
  const {
    jobPost,
    jobApplication,
    rounds,
    interviewSessions,
    interviewSchedule,
  } = useCurrentApplication();

  const getCTAMessage = (): CTAData | null => {
    if (!jobPost) return null;

    // NOT APPLIED
    if (!jobApplication) {
      return {
        messageIcon: <RocketOutlined />,
        heading: messages.notApplied.title,
        description: messages.notApplied.message,
      };
    }

    // RESUME NOT UPLOADED
    if (
      jobApplication.status === "applied" &&
      !jobApplication.profileMatchStatus &&
      !jobApplication.profileId
    ) {
      return {
        messageIcon: <CloudUploadOutlined />,
        heading: messages.resumeNotUploaded.title,
        description: messages.resumeNotUploaded.message,
      };
    }

    // REJECTED
    if (jobApplication.status === "rejected") {
      return {
        messageIcon: <FileSearchOutlined />,
        heading: messages.applicationRejected.title,
        description: messages.applicationRejected.message,
      };
    }

    // SHORTLISTED
    if (jobApplication.status === "shortlisted") {
      return {
        messageIcon: <CheckOutlined />,
        heading: messages.shortlisted.title,
        description: messages.shortlisted.message,
      };
    }

    // HIRED
    if (jobApplication.status === "hired") {
      return {
        messageIcon: <CheckOutlined />,
        heading: messages.hired.title,
        description: messages.hired.message,
      };
    }

    // PARTIALLY QUALIFIED
    if (
      jobApplication.status === "applied" &&
      jobApplication.questionnaireStatus === "partially_qualified"
    ) {
      return {
        messageIcon: <HourglassOutlined />,
        heading: messages.partiallyQualified.title,
        description: messages.partiallyQualified.message,
      };
    }

    // ASSESSMENT NOT GENERATED
    if (
      jobApplication.status === "applied" &&
      jobApplication.questionnaireStatus === "action_required"
    ) {
      return {
        messageIcon: <HourglassOutlined />,
        heading: messages.assessmentNotGenerated.title,
        description: messages.assessmentNotGenerated.message,
      };
    }

    // ASSESSMENT
    if (
      jobApplication.status === "applied" &&
      jobApplication.questionnaireStatus === "pending" &&
      rounds?.length > 0 &&
      interviewSessions?.length > 0
    ) {
      const { roundName } = getLastPendingRoundInfo(interviewSessions, rounds);

      return {
        messageIcon: <CheckOutlined />,
        heading: `You're a good fit, ${user?.firstName}!`,
        description: `Our AI analysis indicates that you are a strong match for this role. Please complete the ${roundName} below to proceed to the interview stage.`,
      };
    }

    // INTERVIEW
    if (jobPost.onlineInterview) {
      if (
        interviewSchedule &&
        jobApplication.interviewSchedule &&
        jobApplication.status === "interview"
      ) {
        const { date, start, end } = getScheduleInformation(interviewSchedule);

        return {
          messageIcon: <CalendarOutlined />,
          heading: `Interview Scheduled!`,
          description: `Great news! Your interview with our hiring team is scheduled on ${date} from ${start} to ${end}. Please make sure you are available at the scheduled time.`,
        };
      }

      if (
        jobApplication.status === "interview" &&
        !jobApplication.interviewSchedule
      ) {
        return {
          messageIcon: <MailOutlined />,
          heading: messages.slotSelection.title,
          description: messages.slotSelection.message,
        };
      }
    }

    return null;
  };

  const ctaData = getCTAMessage();

  if (!ctaData) {
    return null;
  }

  return (
    <div className="job-posts-details__cta-card">
      <div className="job-posts-details__cta-icon-wrap">
        {ctaData.messageIcon}
      </div>
      <div className="job-posts-details__cta-text">
        <Typography.Title
          level={5}
          style={{ margin: 0, color: "var(--ant-color-primary)" }}
          className="job-posts-details__cta-heading"
        >
          {ctaData.heading}
        </Typography.Title>
        <Typography.Text style={{ margin: 0, fontWeight: 500 }}>
          {ctaData.description}
        </Typography.Text>
      </div>
    </div>
  );
};

export default ApplicationJourneyNotification;
